"use client";

import type {
  Flashcard,
  FlashcardCollection,
  FlashcardDeck,
  FlashcardMedia,
  FlashcardNote,
  FlashcardNoteType,
  FlashcardRating,
  FlashcardReview,
} from "@japa-reader/contracts";
import {
  BarChart3,
  BookCopy,
  Check,
  ChevronLeft,
  CirclePlus,
  Clock3,
  Download,
  Edit3,
  Eye,
  FileUp,
  Flag,
  FolderPlus,
  Layers3,
  LoaderCircle,
  MoreHorizontal,
  PauseCircle,
  Play,
  Plus,
  Search,
  Settings2,
  Shuffle,
  Sparkles,
  Tag,
  Trash2,
  Undo2,
  Volume2,
  X,
} from "lucide-react";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import JSZip from "jszip";
import {
  BASIC_NOTE_TYPE_ID,
  CLOZE_NOTE_TYPE_ID,
  deleteFlashcardDeck,
  deleteFlashcardNote,
  deleteFlashcardNoteType,
  deleteFlashcardReview,
  deleteFlashcardMedia,
  loadFlashcardCollection,
  makeFlashcardId,
  newFlashcardDeck,
  newFlashcardNote,
  reconcileNoteCards,
  recordFlashcardReview,
  saveFlashcardCard,
  saveFlashcardDeck,
  saveFlashcardMedia,
  saveFlashcardNote,
  saveFlashcardNoteType,
} from "../lib/flashcards";
import {
  applyLeechRule,
  previewFlashcardRatings,
  resetFlashcard,
  restoreCardBeforeReview,
  scheduleFlashcardReview,
  setFlashcardDue,
  startOfNextDay,
} from "../lib/flashcard-scheduler";
import { deckPath, searchFlashcards } from "../lib/flashcard-search";
import { buildStudyQueue, deckCounts, descendantDeckIds, shouldRepeatLearningCardInSession } from "../lib/flashcard-study";
import { cardTemplateCss, fieldPlainText, renderFlashcardSide, type FlashcardAudioCue } from "../lib/flashcard-templates";
import { compareTypedAnswer } from "../lib/flashcard-typing";
import { NoteTypeDialog } from "./NoteTypeDialog";
import { FlashcardBrowser } from "./FlashcardBrowser";

type FlashcardPage = "decks" | "browse" | "stats" | "types";
type StudyAction = {
  cardBefore: Flashcard;
  review: FlashcardReview;
  siblingsBefore: Flashcard[];
  noteBefore: FlashcardNote | null;
  indexBefore: number;
  queueBefore: string[];
};

export type FlashcardSeed = {
  front: string;
  back: string;
  tags: string[];
  source?: FlashcardNote["source"];
};

const EMPTY_COLLECTION: FlashcardCollection = { decks: [], noteTypes: [], notes: [], cards: [], reviews: [], media: [] };

export function Flashcards({ initialDraft, onInitialDraftConsumed }: { initialDraft?: FlashcardSeed | null; onInitialDraftConsumed?: () => void }) {
  const [collection, setCollection] = useState<FlashcardCollection>(EMPTY_COLLECTION);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState<FlashcardPage>("decks");
  const [selectedDeckId, setSelectedDeckId] = useState<string | null>(null);
  const [deckDialog, setDeckDialog] = useState<FlashcardDeck | null>(null);
  const [noteDialog, setNoteDialog] = useState<FlashcardNote | null>(null);
  const [noteDraftFields, setNoteDraftFields] = useState<Record<string, string>>({});
  const [noteTags, setNoteTags] = useState("");
  const [activeNoteField, setActiveNoteField] = useState("");
  const [dialogError, setDialogError] = useState("");
  const [saving, setSaving] = useState(false);
  const [browseQuery, setBrowseQuery] = useState("");
  const [selectedCardIds, setSelectedCardIds] = useState<Set<string>>(new Set());
  const [typeDraft, setTypeDraft] = useState<FlashcardNoteType | null>(null);
  const [studyCardIds, setStudyCardIds] = useState<string[]>([]);
  const [studyIndex, setStudyIndex] = useState(0);
  const [answerVisible, setAnswerVisible] = useState(false);
  const [studyStartedAt, setStudyStartedAt] = useState(0);
  const [typedAnswer, setTypedAnswer] = useState("");
  const [elapsedAnswerSeconds, setElapsedAnswerSeconds] = useState(0);
  const [lastAction, setLastAction] = useState<StudyAction | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [cardInfoOpen, setCardInfoOpen] = useState(false);
  const [customStudyOpen, setCustomStudyOpen] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);
  const mediaRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    try {
      const next = await loadFlashcardCollection();
      setCollection(next);
      setSelectedDeckId((current) => current && next.decks.some((deck) => deck.id === current) ? current : null);
      setError("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Não foi possível carregar os flashcards.");
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!ready || !initialDraft || !collection.decks.length || !collection.noteTypes.length) return;
    const deckId = collection.decks.find((deck) => deck.kind === "standard")?.id;
    const type = collection.noteTypes.find((candidate) => candidate.id === BASIC_NOTE_TYPE_ID) || collection.noteTypes[0];
    if (!deckId || !type) return;
    const note = { ...newFlashcardNote(deckId, type.id), source: initialDraft.source };
    setNoteDialog(note);
    setNoteDraftFields(Object.fromEntries(type.fields.map((field) => [field.name, field.ordinal === 0 ? initialDraft.front : field.ordinal === 1 ? initialDraft.back : ""])));
    setActiveNoteField(type.fields[0]?.name || "");
    setNoteTags(initialDraft.tags.join(" "));
    setDialogError("");
    onInitialDraftConsumed?.();
  }, [collection.decks, collection.noteTypes, initialDraft, onInitialDraftConsumed, ready]);

  const selectedDeck = collection.decks.find((deck) => deck.id === selectedDeckId) ?? null;
  const currentStudyCard = collection.cards.find((card) => card.id === studyCardIds[studyIndex]) ?? null;
  const currentStudyNote = collection.notes.find((note) => note.id === currentStudyCard?.noteId) ?? null;
  const currentStudyType = collection.noteTypes.find((type) => type.id === currentStudyNote?.noteTypeId) ?? null;
  const currentStudyHomeDeck = collection.decks.find((deck) => deck.id === currentStudyCard?.deckId) ?? null;
  const currentStudyDeck = selectedDeck?.kind === "filtered" ? selectedDeck : currentStudyHomeDeck ?? selectedDeck;
  const ratingPreviews = currentStudyCard && currentStudyDeck
    ? previewFlashcardRatings(currentStudyCard, currentStudyDeck.options)
    : [];
  const browseCards = useMemo(() => searchFlashcards(collection, browseQuery), [browseQuery, collection]);

  useEffect(() => {
    if (!currentStudyCard) return;
    const listener = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches("input,textarea,select,[contenteditable=true]")) return;
      if ((event.key === " " || event.key === "Enter") && !answerVisible) {
        event.preventDefault();
        setAnswerVisible(true);
        return;
      }
      if (answerVisible && ["1", "2", "3", "4"].includes(event.key)) {
        event.preventDefault();
        void answerCard(Number(event.key) as FlashcardRating);
        return;
      }
      if (event.key.toLowerCase() === "e") openNoteEditor(currentStudyNote);
      if (event.key.toLowerCase() === "z") void undoLastReview();
      if (event.key === "-") void buryCurrent(false);
      if (event.key === "=") void buryCurrent(true);
      if (event.key === "!") void suspendCurrent(true);
      if (event.key === "@") void suspendCurrent(false);
      if (event.key === "*") void toggleMarked();
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  });

  useEffect(() => {
    setTypedAnswer("");
    setElapsedAnswerSeconds(0);
  }, [currentStudyCard?.id]);

  useEffect(() => {
    if (!currentStudyCard || !currentStudyDeck?.options.showAnswerTimer) return;
    const update = () => setElapsedAnswerSeconds(Math.max(0, Math.floor((Date.now() - studyStartedAt) / 1_000)));
    update();
    const interval = window.setInterval(update, 500);
    return () => window.clearInterval(interval);
  }, [currentStudyCard, currentStudyDeck?.options.showAnswerTimer, studyStartedAt]);

  useEffect(() => {
    const seconds = currentStudyDeck?.options.autoAdvanceSeconds || 0;
    if (!currentStudyCard || answerVisible || seconds <= 0) return;
    const remaining = Math.max(0, seconds * 1_000 - (Date.now() - studyStartedAt));
    const timer = window.setTimeout(() => setAnswerVisible(true), remaining);
    return () => window.clearTimeout(timer);
  }, [answerVisible, currentStudyCard, currentStudyDeck?.options.autoAdvanceSeconds, studyStartedAt]);

  useEffect(() => {
    if (!currentStudyCard || !currentStudyNote || !currentStudyType || !currentStudyDeck?.options.autoPlayAudio) return;
    const rendered = renderFlashcardSide({ card: currentStudyCard, note: currentStudyNote, noteType: currentStudyType, deck: currentStudyDeck, media: collection.media, side: answerVisible ? "back" : "front" });
    void playFlashcardAudio(rendered.audioCues);
    return stopFlashcardAudio;
  }, [answerVisible, currentStudyCard, currentStudyDeck, currentStudyNote, currentStudyType]);

  function showPage(next: FlashcardPage) {
    setPage(next);
    setSelectedDeckId(null);
    endStudy();
  }

  function openDeckCreator(parentId: string | null = null) {
    setDialogError("");
    setDeckDialog(newFlashcardDeck("", parentId));
  }

  function openDeckEditor(deck: FlashcardDeck) {
    setDialogError("");
    setDeckDialog({ ...deck, options: { ...deck.options } });
  }

  async function submitDeck() {
    if (!deckDialog?.name.trim()) return setDialogError("Informe o nome do baralho.");
    setSaving(true);
    try {
      const deck = { ...deckDialog, name: deckDialog.name.trim(), updatedAt: Date.now() };
      const presetSiblings = collection.decks
        .filter((candidate) => candidate.id !== deck.id && candidate.optionsPresetName === deck.optionsPresetName)
        .map((candidate) => ({ ...candidate, options: { ...deck.options }, updatedAt: Date.now() }));
      await Promise.all([saveFlashcardDeck(deck), ...presetSiblings.map(saveFlashcardDeck)]);
      setCollection((current) => ({
        ...current,
        decks: current.decks
          .map((candidate) => presetSiblings.find((sibling) => sibling.id === candidate.id) || candidate)
          .some((candidate) => candidate.id === deck.id)
          ? current.decks.map((candidate) => candidate.id === deck.id ? deck : presetSiblings.find((sibling) => sibling.id === candidate.id) || candidate)
          : [...current.decks.map((candidate) => presetSiblings.find((sibling) => sibling.id === candidate.id) || candidate), deck],
      }));
      setDeckDialog(null);
      setSelectedDeckId(deck.id);
      setDialogError("");
    } catch (reason) {
      setDialogError(errorMessage(reason));
    } finally {
      setSaving(false);
    }
  }

  async function removeDeck(deck: FlashcardDeck) {
    if (!window.confirm(`Excluir “${deck.name}”, seus subbaralhos e todos os cartões?`)) return;
    try {
      await deleteFlashcardDeck(deck.id);
      await refresh();
      setSelectedDeckId(null);
    } catch (reason) {
      setError(errorMessage(reason));
    }
  }

  function openNoteEditor(note?: FlashcardNote | null, deckId = selectedDeckId) {
    const target = note || newFlashcardNote(deckId || collection.decks[0]?.id || "deck-default");
    const noteType = collection.noteTypes.find((type) => type.id === target.noteTypeId) || collection.noteTypes[0];
    const fields = Object.fromEntries((noteType?.fields || []).map((field) => [field.name, target.fields[field.name] || ""]));
    setNoteDialog({ ...target });
    setNoteDraftFields(fields);
    setActiveNoteField(noteType?.fields[0]?.name || "");
    setNoteTags(target.tags.join(" "));
    setDialogError("");
  }

  function changeNoteType(noteTypeId: string) {
    const type = collection.noteTypes.find((candidate) => candidate.id === noteTypeId);
    if (!noteDialog || !type) return;
    setNoteDialog({ ...noteDialog, noteTypeId });
    setNoteDraftFields(Object.fromEntries(type.fields.map((field) => [field.name, noteDraftFields[field.name] || ""])));
    setActiveNoteField(type.fields[0]?.name || "");
  }

  async function attachMedia(event: ChangeEvent<HTMLInputElement>) {
    const files = [...(event.target.files || [])];
    event.target.value = "";
    if (!files.length || !noteDialog) return;
    const targetField = activeNoteField || collection.noteTypes.find((type) => type.id === noteDialog.noteTypeId)?.fields[0]?.name;
    if (!targetField) return setDialogError("Selecione um campo antes de anexar a mídia.");
    setSaving(true);
    try {
      const added: FlashcardMedia[] = [];
      for (const file of files) added.push(await saveFlashcardMedia(file));
      const markup = added.map(mediaMarkup).join("\n");
      setCollection((current) => ({ ...current, media: [...current.media, ...added] }));
      setNoteDraftFields((fields) => ({ ...fields, [targetField]: `${fields[targetField] || ""}${fields[targetField] ? "\n" : ""}${markup}` }));
      setDialogError("");
    } catch (reason) {
      setDialogError(errorMessage(reason));
    } finally {
      setSaving(false);
    }
  }

  async function removeMedia(media: FlashcardMedia) {
    const reference = `media:${media.id}`;
    const usedElsewhere = collection.notes.some((note) => note.id !== noteDialog?.id && Object.values(note.fields).some((value) => value.includes(reference)));
    if (usedElsewhere) return setDialogError("Esta mídia também é usada por outra nota e não pode ser excluída aqui.");
    if (!window.confirm(`Excluir a mídia “${media.name}”?`)) return;
    try {
      await deleteFlashcardMedia(media);
      setCollection((current) => ({ ...current, media: current.media.filter((item) => item.id !== media.id) }));
      setNoteDraftFields((fields) => Object.fromEntries(Object.entries(fields).map(([name, value]) => [name, removeMediaMarkup(value, media.id)])));
    } catch (reason) {
      setDialogError(errorMessage(reason));
    }
  }

  function insertCloze(fieldName: string) {
    const value = noteDraftFields[fieldName] || "";
    const nextOrdinal = Math.max(0, ...[...value.matchAll(/\{\{c(\d+)::/gi)].map((match) => Number(match[1]))) + 1;
    setNoteDraftFields((fields) => ({ ...fields, [fieldName]: `${value}${value ? " " : ""}{{c${nextOrdinal}::texto}}` }));
  }

  async function submitNote() {
    if (!noteDialog) return;
    const noteType = collection.noteTypes.find((type) => type.id === noteDialog.noteTypeId);
    if (!noteType) return setDialogError("Selecione um tipo de nota válido.");
    if (!Object.values(noteDraftFields).some((value) => stripHtml(value).trim())) return setDialogError("Preencha pelo menos um campo.");
    const firstField = noteType.fields[0]?.name;
    const duplicate = firstField && collection.notes.some((note) => note.id !== noteDialog.id
      && note.noteTypeId === noteDialog.noteTypeId
      && stripHtml(note.fields[firstField] || "").trim() === stripHtml(noteDraftFields[firstField] || "").trim());
    if (duplicate && !window.confirm("Já existe uma nota com o mesmo primeiro campo. Salvar mesmo assim?")) return;
    const note: FlashcardNote = {
      ...noteDialog,
      fields: noteDraftFields,
      tags: normalizeTags(noteTags),
      updatedAt: Date.now(),
    };
    const existing = collection.cards.filter((card) => card.noteId === note.id);
    const { cards, removedCardIds } = reconcileNoteCards(note, noteType, existing);
    if (!cards.length) return setDialogError(noteType.kind === "cloze" ? "Crie pelo menos uma omissão {{c1::assim}}." : "O modelo não gerou nenhum cartão.");
    setSaving(true);
    try {
      await saveFlashcardNote(note, cards, removedCardIds);
      setCollection((current) => ({
        ...current,
        notes: upsert(current.notes, note),
        cards: [...current.cards.filter((card) => card.noteId !== note.id), ...cards],
      }));
      setNoteDialog(null);
      setDialogError("");
    } catch (reason) {
      setDialogError(errorMessage(reason));
    } finally {
      setSaving(false);
    }
  }

  function startStudy(deck: FlashcardDeck, query = "") {
    const cards = buildStudyQueue(collection, deck, query);
    setSelectedDeckId(deck.id);
    setStudyCardIds(cards.map((card) => card.id));
    setStudyIndex(0);
    setAnswerVisible(false);
    setTypedAnswer("");
    setStudyStartedAt(Date.now());
    setLastAction(null);
    setCustomStudyOpen(false);
  }

  function endStudy() {
    setStudyCardIds([]);
    setStudyIndex(0);
    setAnswerVisible(false);
    setTypedAnswer("");
    setMoreOpen(false);
  }

  async function answerCard(rating: FlashcardRating) {
    if (!currentStudyCard || !currentStudyDeck) return;
    const now = Date.now();
    const rawDuration = now - studyStartedAt;
    const maximumDuration = currentStudyDeck.options.maximumAnswerSeconds > 0 ? currentStudyDeck.options.maximumAnswerSeconds * 1_000 : rawDuration;
    const scheduled = scheduleFlashcardReview(currentStudyCard, currentStudyDeck.options, rating, Math.min(rawDuration, maximumDuration), now);
    const previewOnly = currentStudyDeck.kind === "filtered" && !currentStudyDeck.rescheduleFilteredCards;
    let card = previewOnly ? { ...currentStudyCard, updatedAt: now } : scheduled.card;
    const review = previewOnly ? {
      ...scheduled.review,
      dueAfter: currentStudyCard.due,
      scheduledDaysAfter: currentStudyCard.scheduledDays,
      stabilityAfter: currentStudyCard.stability,
      difficultyAfter: currentStudyCard.difficulty,
    } : scheduled.review;
    const leech = previewOnly
      ? { card, note: currentStudyNote, triggered: false }
      : applyLeechRule(card, currentStudyNote, currentStudyDeck.options, rating, now);
    card = leech.card;
    const noteBefore = leech.triggered ? currentStudyNote : null;
    const noteAfter = leech.triggered ? leech.note : null;
    const siblings = collection.cards.filter((candidate) => candidate.noteId === card.noteId && candidate.id !== card.id);
    const shouldBury = currentStudyCard.state === "new" ? currentStudyDeck.options.buryNewSiblings : currentStudyDeck.options.buryReviewSiblings;
    const siblingsBefore = shouldBury ? siblings.filter((sibling) => !sibling.suspended && (!sibling.buriedUntil || sibling.buriedUntil <= now)) : [];
    const buriedSiblings = siblingsBefore.map((sibling) => ({ ...sibling, buriedUntil: startOfNextDay(), updatedAt: now }));
    setCollection((current) => ({
      ...current,
      cards: current.cards.map((candidate) => candidate.id === card.id ? card : buriedSiblings.find((sibling) => sibling.id === candidate.id) || candidate),
      notes: noteAfter ? upsert(current.notes, noteAfter) : current.notes,
      reviews: [...current.reviews, review],
    }));
    const queueBefore = studyCardIds;
    if (!previewOnly && shouldRepeatLearningCardInSession(card, now)) setStudyCardIds((ids) => [...ids, card.id]);
    setLastAction({ cardBefore: currentStudyCard, review, siblingsBefore, noteBefore, indexBefore: studyIndex, queueBefore });
    setStudyIndex((index) => index + 1);
    setAnswerVisible(false);
    setTypedAnswer("");
    setStudyStartedAt(Date.now());
    setMoreOpen(false);
    try {
      await Promise.all([
        recordFlashcardReview(card, review),
        ...buriedSiblings.map(saveFlashcardCard),
        ...(noteAfter ? [saveFlashcardNote(noteAfter, [])] : []),
      ]);
    } catch (reason) {
      setError(`A revisão ficou salva neste dispositivo, mas houve um problema de sincronização: ${errorMessage(reason)}`);
    }
  }

  async function undoLastReview() {
    if (!lastAction) return;
    const restored = restoreCardBeforeReview(
      collection.cards.find((card) => card.id === lastAction.cardBefore.id) || lastAction.cardBefore,
      lastAction.review,
    );
    setCollection((current) => ({
      ...current,
      cards: current.cards.map((card) => {
        if (card.id === restored.id) return restored;
        return lastAction.siblingsBefore.find((sibling) => sibling.id === card.id) || card;
      }),
      notes: lastAction.noteBefore ? upsert(current.notes, lastAction.noteBefore) : current.notes,
      reviews: current.reviews.filter((review) => review.id !== lastAction.review.id),
    }));
    setStudyIndex(lastAction.indexBefore);
    setStudyCardIds(lastAction.queueBefore);
    setAnswerVisible(false);
    setTypedAnswer("");
    setLastAction(null);
    await Promise.all([
      deleteFlashcardReview(lastAction.review.id),
      saveFlashcardCard(restored),
      ...lastAction.siblingsBefore.map(saveFlashcardCard),
      ...(lastAction.noteBefore ? [saveFlashcardNote(lastAction.noteBefore, [])] : []),
    ]).catch((reason) => setError(errorMessage(reason)));
  }

  async function buryCurrent(noteWide: boolean) {
    if (!currentStudyCard) return;
    const ids = noteWide
      ? collection.cards.filter((card) => card.noteId === currentStudyCard.noteId).map((card) => card.id)
      : [currentStudyCard.id];
    await updateCards(ids, (card) => ({ ...card, suspended: false, buriedUntil: startOfNextDay(), updatedAt: Date.now() }));
    setStudyIndex((index) => index + 1);
    setAnswerVisible(false);
  }

  async function suspendCurrent(noteWide: boolean) {
    if (!currentStudyCard) return;
    const ids = noteWide
      ? collection.cards.filter((card) => card.noteId === currentStudyCard.noteId).map((card) => card.id)
      : [currentStudyCard.id];
    await updateCards(ids, (card) => ({ ...card, suspended: true, buriedUntil: null, updatedAt: Date.now() }));
    setStudyIndex((index) => index + 1);
    setAnswerVisible(false);
  }

  async function toggleMarked() {
    if (!currentStudyNote) return;
    const marked = currentStudyNote.tags.includes("marked");
    const note = { ...currentStudyNote, tags: marked ? currentStudyNote.tags.filter((tag) => tag !== "marked") : [...currentStudyNote.tags, "marked"], updatedAt: Date.now() };
    const cards = collection.cards.filter((card) => card.noteId === note.id);
    await saveFlashcardNote(note, cards);
    setCollection((current) => ({ ...current, notes: upsert(current.notes, note) }));
  }

  async function updateCards(ids: string[], update: (card: Flashcard) => Flashcard) {
    const idSet = new Set(ids);
    const changed = collection.cards.filter((card) => idSet.has(card.id)).map(update);
    setCollection((current) => ({ ...current, cards: current.cards.map((card) => changed.find((candidate) => candidate.id === card.id) || card) }));
    await Promise.all(changed.map(saveFlashcardCard)).catch((reason) => setError(errorMessage(reason)));
  }

  async function bulkSuspend() {
    const ids = [...selectedCardIds];
    const shouldSuspend = ids.some((id) => !collection.cards.find((card) => card.id === id)?.suspended);
    await updateCards(ids, (card) => ({ ...card, suspended: shouldSuspend, buriedUntil: null, updatedAt: Date.now() }));
  }

  async function bulkFlag(flag: Flashcard["flag"]) {
    await updateCards([...selectedCardIds], (card) => ({ ...card, flag, updatedAt: Date.now() }));
  }

  async function bulkMove(deckId: string) {
    if (!deckId) return;
    const noteIds = new Set([...selectedCardIds].map((id) => collection.cards.find((card) => card.id === id)?.noteId).filter(Boolean));
    const notes = collection.notes.filter((note) => noteIds.has(note.id)).map((note) => ({ ...note, deckId, updatedAt: Date.now() }));
    for (const note of notes) {
      const type = collection.noteTypes.find((candidate) => candidate.id === note.noteTypeId);
      if (!type) continue;
      const existing = collection.cards.filter((card) => card.noteId === note.id);
      const result = reconcileNoteCards(note, type, existing);
      await saveFlashcardNote(note, result.cards, result.removedCardIds);
    }
    await refresh();
  }

  async function bulkDelete() {
    const noteIds = [...new Set([...selectedCardIds].map((id) => collection.cards.find((card) => card.id === id)?.noteId).filter((id): id is string => Boolean(id)))];
    if (!noteIds.length || !window.confirm(`Excluir ${noteIds.length} nota(s) e todo o histórico associado?`)) return;
    await Promise.all(noteIds.map(deleteFlashcardNote));
    setSelectedCardIds(new Set());
    await refresh();
  }

  async function resetSelected() {
    await updateCards([...selectedCardIds], (card) => resetFlashcard(card, false));
  }

  async function setSelectedDue() {
    const input = window.prompt("Em quantos dias estes cartões devem aparecer? Use 0 para hoje.", "0");
    if (input === null) return;
    const days = Number(input);
    if (!Number.isFinite(days)) return;
    await updateCards([...selectedCardIds], (card) => setFlashcardDue(card, startOfToday() + days * 86_400_000));
  }

  function selectedNotes() {
    const noteIds = new Set([...selectedCardIds].map((id) => collection.cards.find((card) => card.id === id)?.noteId).filter(Boolean));
    return collection.notes.filter((note) => noteIds.has(note.id));
  }

  async function persistEditedNotes(notes: FlashcardNote[]) {
    const results = notes.map((note) => {
      const type = collection.noteTypes.find((candidate) => candidate.id === note.noteTypeId);
      if (!type) throw new Error(`O tipo de nota de “${Object.values(note.fields)[0] || note.id}” não existe.`);
      return { note, ...reconcileNoteCards(note, type, collection.cards.filter((card) => card.noteId === note.id)) };
    });
    for (const result of results) await saveFlashcardNote(result.note, result.cards, result.removedCardIds);
    const affectedIds = new Set(results.map((result) => result.note.id));
    const removedCardIds = new Set(results.flatMap((result) => result.removedCardIds));
    setCollection((current) => ({
      ...current,
      notes: current.notes.map((note) => results.find((result) => result.note.id === note.id)?.note || note),
      cards: [...current.cards.filter((card) => !affectedIds.has(card.noteId)), ...results.flatMap((result) => result.cards)],
      reviews: current.reviews.filter((review) => !removedCardIds.has(review.cardId)),
    }));
  }

  async function bulkAddTags() {
    const input = window.prompt("Tags para adicionar, separadas por espaço ou vírgula:", "");
    if (input === null) return;
    const tags = normalizeTags(input);
    if (!tags.length) return;
    await persistEditedNotes(selectedNotes().map((note) => ({ ...note, tags: [...new Set([...note.tags, ...tags])], updatedAt: Date.now() }))).catch((reason) => setError(errorMessage(reason)));
  }

  async function bulkRemoveTags() {
    const input = window.prompt("Tags para remover, separadas por espaço ou vírgula:", "");
    if (input === null) return;
    const tags = new Set(normalizeTags(input).map((tag) => tag.toLocaleLowerCase()));
    if (!tags.size) return;
    await persistEditedNotes(selectedNotes().map((note) => ({ ...note, tags: note.tags.filter((tag) => !tags.has(tag.toLocaleLowerCase())), updatedAt: Date.now() }))).catch((reason) => setError(errorMessage(reason)));
  }

  async function bulkFindReplace() {
    const find = window.prompt("Texto a localizar nos campos selecionados:", "");
    if (!find) return;
    const replacement = window.prompt("Substituir por:", "");
    if (replacement === null) return;
    const fieldName = window.prompt("Nome do campo, ou * para todos:", "*")?.trim() || "*";
    const expression = parseReplacementExpression(find);
    const notes = selectedNotes().map((note) => ({
      ...note,
      fields: Object.fromEntries(Object.entries(note.fields).map(([name, value]) => [name, fieldName === "*" || name.toLocaleLowerCase() === fieldName.toLocaleLowerCase() ? value.replace(expression, replacement) : value])),
      updatedAt: Date.now(),
    }));
    await persistEditedNotes(notes).catch((reason) => setError(errorMessage(reason)));
  }

  async function bulkChangeNoteType(noteTypeId: string) {
    const target = collection.noteTypes.find((type) => type.id === noteTypeId);
    if (!target) return;
    const notes = selectedNotes().map((note) => {
      const source = collection.noteTypes.find((type) => type.id === note.noteTypeId);
      return {
        ...note,
        noteTypeId,
        fields: Object.fromEntries(target.fields.map((field, index) => [field.name, note.fields[field.name] ?? note.fields[source?.fields[index]?.name || ""] ?? ""])),
        updatedAt: Date.now(),
      };
    });
    await persistEditedNotes(notes).catch((reason) => setError(errorMessage(reason)));
  }

  async function bulkReposition() {
    const input = window.prompt("Primeira posição para os cartões novos selecionados:", "1");
    if (input === null) return;
    const start = Math.max(1, Math.floor(Number(input)));
    if (!Number.isFinite(start)) return;
    const ids = [...selectedCardIds].filter((id) => collection.cards.find((card) => card.id === id)?.state === "new");
    await updateCards(ids, (card) => ({ ...card, position: start + ids.indexOf(card.id), updatedAt: Date.now() }));
  }

  function findDuplicates() {
    const groups = new Map<string, FlashcardNote[]>();
    for (const note of collection.notes) {
      const type = collection.noteTypes.find((candidate) => candidate.id === note.noteTypeId);
      const value = stripHtml(note.fields[type?.fields[0]?.name || ""] || "").trim().normalize("NFKC").toLocaleLowerCase();
      if (value) groups.set(value, [...(groups.get(value) || []), note]);
    }
    const noteIds = new Set([...groups.values()].filter((notes) => notes.length > 1).flat().map((note) => note.id));
    const cards = collection.cards.filter((card) => noteIds.has(card.noteId));
    setBrowseQuery(cards.length ? cards.map((card) => `cid:${card.id}`).join(" OR ") : "nid:__nenhuma-duplicata__");
    setSelectedCardIds(new Set(cards.map((card) => card.id)));
  }

  function editNoteType(noteType: FlashcardNoteType) {
    setTypeDraft(structuredClone(noteType));
    setDialogError("");
  }

  function createNoteType(kind: FlashcardNoteType["kind"]) {
    const source = collection.noteTypes.find((type) => type.id === (kind === "cloze" ? CLOZE_NOTE_TYPE_ID : BASIC_NOTE_TYPE_ID));
    if (!source) return;
    const now = Date.now();
    setTypeDraft({
      ...structuredClone(source),
      id: makeFlashcardId("note-type"),
      name: `${source.name} personalizado`,
      fields: source.fields.map((field) => ({ ...field, id: `field-${crypto.randomUUID()}` })),
      templates: source.templates.map((template) => ({ ...template, id: `template-${crypto.randomUUID()}` })),
      createdAt: now,
      updatedAt: now,
    });
  }

  async function submitNoteType() {
    if (!typeDraft?.name.trim() || !typeDraft.fields.length || !typeDraft.templates.length) return setDialogError("O tipo precisa de nome, campos e modelos.");
    const fieldNames = typeDraft.fields.map((field) => field.name.trim().toLocaleLowerCase());
    const templateNames = typeDraft.templates.map((template) => template.name.trim().toLocaleLowerCase());
    if (fieldNames.some((name) => !name) || new Set(fieldNames).size !== fieldNames.length) return setDialogError("Os campos precisam de nomes únicos.");
    if (templateNames.some((name) => !name) || new Set(templateNames).size !== templateNames.length) return setDialogError("Os modelos precisam de nomes únicos.");
    const type = {
      ...typeDraft,
      name: typeDraft.name.trim(),
      fields: typeDraft.fields.map((field, ordinal) => ({ ...field, name: field.name.trim(), ordinal })),
      templates: typeDraft.templates.map((template, ordinal) => ({ ...template, name: template.name.trim(), ordinal })),
      updatedAt: Date.now(),
    };
    const previous = collection.noteTypes.find((candidate) => candidate.id === type.id);
    const oldFields = new Map(previous?.fields.map((field) => [field.id, field]) || []);
    const affected = collection.notes.filter((note) => note.noteTypeId === type.id).map((note) => {
      const migrated: FlashcardNote = {
        ...note,
        fields: Object.fromEntries(type.fields.map((field) => {
          const oldName = oldFields.get(field.id)?.name || field.name;
          return [field.name, note.fields[oldName] ?? note.fields[field.name] ?? ""];
        })),
        updatedAt: Date.now(),
      };
      const result = reconcileNoteCards(migrated, type, collection.cards.filter((card) => card.noteId === note.id));
      return { note: migrated, ...result };
    });
    const removedCardIds = new Set(affected.flatMap((item) => item.removedCardIds));
    setSaving(true);
    try {
      await saveFlashcardNoteType(type);
      for (const item of affected) await saveFlashcardNote(item.note, item.cards, item.removedCardIds);
      setCollection((current) => ({
        ...current,
        noteTypes: upsert(current.noteTypes, type),
        notes: current.notes.map((note) => affected.find((item) => item.note.id === note.id)?.note || note),
        cards: [
          ...current.cards.filter((card) => !affected.some((item) => item.note.id === card.noteId)),
          ...affected.flatMap((item) => item.cards),
        ],
        reviews: current.reviews.filter((review) => !removedCardIds.has(review.cardId)),
      }));
      setTypeDraft(null);
    } catch (reason) {
      setDialogError(errorMessage(reason));
    } finally {
      setSaving(false);
    }
  }

  async function removeNoteType(type: FlashcardNoteType) {
    if (type.id === BASIC_NOTE_TYPE_ID || type.id === CLOZE_NOTE_TYPE_ID) return;
    if (!window.confirm(`Excluir o tipo “${type.name}”?`)) return;
    try {
      await deleteFlashcardNoteType(type.id);
      setCollection((current) => ({ ...current, noteTypes: current.noteTypes.filter((candidate) => candidate.id !== type.id) }));
    } catch (reason) {
      setError(errorMessage(reason));
    }
  }

  async function exportCollection(format: "json" | "tsv") {
    if (format === "tsv") {
      const contents = collection.notes.map((note) => {
        const type = collection.noteTypes.find((candidate) => candidate.id === note.noteTypeId);
        return [...(type?.fields || []).map((field) => note.fields[field.name] || ""), note.tags.join(" ")].map(tsvCell).join("\t");
      }).join("\n");
      downloadBlob(new Blob([contents], { type: "text/tab-separated-values" }), "japa-reader-flashcards.tsv");
      return;
    }
    setSaving(true);
    try {
      const zip = new JSZip();
      const portableCollection: FlashcardCollection = {
        ...collection,
        media: collection.media.map(({ blob: _blob, url: _url, ...media }) => ({ ...media, blob: null })),
      };
      zip.file("manifest.json", JSON.stringify({ format: "japa-reader-flashcards", version: 2, exportedAt: Date.now(), collection: portableCollection }, null, 2));
      for (const media of collection.media) {
        const blob = media.blob || (media.url ? await fetch(media.url).then((response) => {
          if (!response.ok) throw new Error(`Não foi possível incluir “${media.name}” no pacote.`);
          return response.blob();
        }) : null);
        if (blob) zip.file(`media/${media.id}`, blob);
      }
      downloadBlob(await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } }), "japa-reader-flashcards.jrfcpkg");
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setSaving(false);
    }
  }

  async function importCollection(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      if (file.name.toLowerCase().endsWith(".jrfcpkg")) {
        let payload: { format?: string; collection?: FlashcardCollection };
        let mediaFiles = new Map<string, Blob>();
        try {
          const zip = await JSZip.loadAsync(file);
          const manifest = zip.file("manifest.json");
          if (!manifest) throw new Error("O pacote não contém um manifesto.");
          payload = JSON.parse(await manifest.async("text")) as { format?: string; collection?: FlashcardCollection };
          for (const media of payload.collection?.media || []) {
            const entry = zip.file(`media/${media.id}`);
            if (entry) mediaFiles.set(media.id, await entry.async("blob"));
          }
        } catch (reason) {
          try {
            payload = JSON.parse(await file.text()) as { format?: string; collection?: FlashcardCollection };
            mediaFiles = new Map();
          } catch {
            throw reason;
          }
        }
        if (payload.format !== "japa-reader-flashcards" || !payload.collection) throw new Error("Pacote de flashcards inválido.");
        await importJsonCollection(payload.collection, mediaFiles);
      } else if (file.name.toLowerCase().endsWith(".json")) {
        const payload = JSON.parse(await file.text()) as { format?: string; collection?: FlashcardCollection };
        if (payload.format !== "japa-reader-flashcards" || !payload.collection) throw new Error("Pacote de flashcards inválido.");
        await importJsonCollection(payload.collection);
      } else {
        await importTsv(await file.text(), file.name.replace(/\.[^.]+$/, ""));
      }
      await refresh();
    } catch (reason) {
      setError(errorMessage(reason));
    }
  }

  async function importJsonCollection(imported: FlashcardCollection, mediaFiles = new Map<string, Blob>()) {
    const mediaIds = new Map<string, string>();
    for (const media of imported.media || []) {
      const blob = mediaFiles.get(media.id);
      if (!blob) continue;
      const saved = await saveFlashcardMedia(new File([blob], media.name, { type: media.contentType, lastModified: media.updatedAt }));
      mediaIds.set(media.id, saved.id);
    }
    for (const deck of imported.decks || []) await saveFlashcardDeck(deck);
    for (const type of imported.noteTypes || []) await saveFlashcardNoteType({
      ...type,
      templates: type.templates.map((template) => ({
        ...template,
        frontTemplate: replaceMediaReferences(template.frontTemplate, mediaIds),
        backTemplate: replaceMediaReferences(template.backTemplate, mediaIds),
      })),
    });
    for (const note of imported.notes || []) {
      const portableNote = { ...note, fields: Object.fromEntries(Object.entries(note.fields).map(([name, value]) => [name, replaceMediaReferences(value, mediaIds)])) };
      const cards = (imported.cards || []).filter((card) => card.noteId === note.id);
      await saveFlashcardNote(portableNote, cards);
    }
    for (const review of imported.reviews || []) {
      const card = imported.cards.find((candidate) => candidate.id === review.cardId);
      if (card) await recordFlashcardReview(card, review);
    }
  }

  async function importTsv(contents: string, name: string) {
    const deck = newFlashcardDeck(name || "Importado");
    await saveFlashcardDeck(deck);
    const type = collection.noteTypes.find((candidate) => candidate.id === BASIC_NOTE_TYPE_ID);
    if (!type) throw new Error("O tipo Básico não está disponível.");
    const rows = contents.split(/\r?\n/).filter(Boolean).map((line) => line.split("\t"));
    for (const [front = "", back = "", tags = ""] of rows) {
      const note = { ...newFlashcardNote(deck.id, type.id), fields: { Frente: front, Verso: back }, tags: normalizeTags(tags) };
      const generated = reconcileNoteCards(note, type, []);
      await saveFlashcardNote(note, generated.cards);
    }
  }

  if (!ready) return <section className="flashcards-loading"><LoaderCircle className="spin" size={26} /> Preparando seus flashcards…</section>;

  if (currentStudyCard && currentStudyNote && currentStudyType && currentStudyDeck) {
    const front = renderFlashcardSide({ card: currentStudyCard, note: currentStudyNote, noteType: currentStudyType, deck: currentStudyDeck, media: collection.media, side: "front" });
    const back = renderFlashcardSide({ card: currentStudyCard, note: currentStudyNote, noteType: currentStudyType, deck: currentStudyDeck, media: collection.media, side: "back" });
    const typedField = front.typeAnswerField;
    const expectedAnswer = typedField ? fieldPlainText(currentStudyNote, typedField) : "";
    const audioCues = answerVisible ? back.audioCues : front.audioCues;
    return (
      <section className="flash-study" aria-label="Sessão de revisão">
        <header className="study-header">
          <button className="text-button" onClick={endStudy}><ChevronLeft size={17} /> Encerrar</button>
          <div><strong>{deckPath(currentStudyDeck, collection.decks)}</strong><small>{studyCardIds.length - studyIndex} restantes{currentStudyDeck.options.showAnswerTimer ? ` · ${Math.min(elapsedAnswerSeconds, currentStudyDeck.options.maximumAnswerSeconds || elapsedAnswerSeconds)}s` : ""}</small></div>
          <div className="study-counts"><span className="count-new">{studyCardIds.slice(studyIndex).filter((id) => collection.cards.find((card) => card.id === id)?.state === "new").length}</span><span className="count-learn">{studyCardIds.slice(studyIndex).filter((id) => ["learning", "relearning"].includes(collection.cards.find((card) => card.id === id)?.state || "")).length}</span><span className="count-review">{studyCardIds.slice(studyIndex).filter((id) => collection.cards.find((card) => card.id === id)?.state === "review").length}</span></div>
        </header>
        <div className={`study-card ${answerVisible ? "is-answer" : ""}`}>
          <style>{cardTemplateCss(currentStudyType, currentStudyCard)}</style>
          <div className="flashcard-render" dangerouslySetInnerHTML={{ __html: answerVisible ? back.html : front.html }} />
          {!answerVisible && typedField && <label className="typed-answer-input"><span>Digite a resposta</span><input autoFocus value={typedAnswer} onChange={(event) => setTypedAnswer(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") setAnswerVisible(true); }} lang="ja" autoComplete="off" /></label>}
          {answerVisible && typedField && <TypedAnswerResult typed={typedAnswer} expected={expectedAnswer} />}
          {currentStudyCard.flag > 0 && <span className={`study-flag flag-${currentStudyCard.flag}`}><Flag size={16} /></span>}
          {currentStudyNote.tags.includes("marked") && <span className="study-mark"><Sparkles size={16} /></span>}
        </div>
        <footer className="study-footer">
          {!answerVisible ? (
            <button className="show-answer-button" onClick={() => setAnswerVisible(true)}><Eye size={18} /> Mostrar resposta <kbd>Espaço</kbd></button>
          ) : (
            <div className="rating-grid">
              {ratingPreviews.map((preview) => <button key={preview.rating} className={`rating-${preview.rating}`} onClick={() => void answerCard(preview.rating)}><small>{preview.intervalLabel}</small><strong>{preview.label}</strong><kbd>{preview.rating}</kbd></button>)}
            </div>
          )}
          <div className="study-tools">
            <button disabled={!audioCues.length} onClick={() => { stopFlashcardAudio(); void playFlashcardAudio(audioCues); }}><Volume2 size={16} /> Repetir áudio</button>
            <button onClick={() => openNoteEditor(currentStudyNote)}><Edit3 size={16} /> Editar</button>
            <button disabled={!lastAction} onClick={() => void undoLastReview()}><Undo2 size={16} /> Desfazer</button>
            <button onClick={() => setMoreOpen((open) => !open)}><MoreHorizontal size={17} /> Mais</button>
          </div>
          {moreOpen && <div className="study-more-menu">
            <button onClick={() => void toggleMarked()}><Sparkles size={15} /> Marcar nota <kbd>*</kbd></button>
            <button onClick={() => void buryCurrent(false)}><Clock3 size={15} /> Ocultar cartão hoje <kbd>-</kbd></button>
            <button onClick={() => void buryCurrent(true)}><BookCopy size={15} /> Ocultar nota hoje <kbd>=</kbd></button>
            <button onClick={() => void suspendCurrent(false)}><PauseCircle size={15} /> Suspender cartão <kbd>@</kbd></button>
            <button onClick={() => void suspendCurrent(true)}><PauseCircle size={15} /> Suspender nota <kbd>!</kbd></button>
            <button onClick={() => setCardInfoOpen(true)}><BarChart3 size={15} /> Informações do cartão</button>
          </div>}
        </footer>
        {cardInfoOpen && <CardInfoDialog card={currentStudyCard} reviews={collection.reviews.filter((review) => review.cardId === currentStudyCard.id)} onClose={() => setCardInfoOpen(false)} />}
        {noteDialog && noteEditorDialog()}
      </section>
    );
  }

  if (studyCardIds.length > 0 && studyIndex >= studyCardIds.length) {
    return <section className="flash-study study-complete" aria-label="Sessão concluída">
      <div className="study-complete-card"><span className="study-complete-icon"><Check size={30} /></span><span className="eyebrow">SESSÃO CONCLUÍDA</span><h1>Revisões em dia.</h1><p>Você respondeu {studyCardIds.length} {studyCardIds.length === 1 ? "cartão" : "cartões"} nesta sessão.</p><div><button className="ghost-button" disabled={!lastAction} onClick={() => void undoLastReview()}><Undo2 size={17} /> Desfazer última resposta</button><button className="align-button" onClick={endStudy}><ChevronLeft size={17} /> Voltar ao baralho</button></div></div>
    </section>;
  }

  const selectedCounts = selectedDeck ? deckCounts(collection, selectedDeck.id) : null;
  return (
    <section className="flashcards-page" aria-labelledby="flashcards-title">
      <div className="flashcards-subnav">
        <div className="flashcards-tabs" role="navigation" aria-label="Flashcards">
          <button className={page === "decks" ? "is-active" : ""} onClick={() => showPage("decks")}><Layers3 size={16} /> Baralhos</button>
          <button className={page === "browse" ? "is-active" : ""} onClick={() => showPage("browse")}><Search size={16} /> Explorar</button>
          <button className={page === "stats" ? "is-active" : ""} onClick={() => showPage("stats")}><BarChart3 size={16} /> Estatísticas</button>
          <button className={page === "types" ? "is-active" : ""} onClick={() => showPage("types")}><Settings2 size={16} /> Tipos de nota</button>
        </div>
        <div className="flashcards-global-actions">
          <button onClick={() => importRef.current?.click()}><FileUp size={16} /> Importar</button>
          <button onClick={() => exportCollection("json")}><Download size={16} /> Exportar</button>
          <input ref={importRef} type="file" accept=".jrfcpkg,.json,.tsv,.txt" hidden onChange={(event) => void importCollection(event)} />
        </div>
      </div>
      {error && <div className="form-error flashcards-error" role="alert">{error}<button onClick={() => setError("")} aria-label="Fechar"><X size={14} /></button></div>}

      {page === "decks" && !selectedDeck && <DeckDashboard collection={collection} onOpen={setSelectedDeckId} onCreate={openDeckCreator} onEdit={openDeckEditor} onDelete={(deck) => void removeDeck(deck)} onAddNote={(deckId) => openNoteEditor(null, deckId)} />}

      {page === "decks" && selectedDeck && selectedCounts && <section className="deck-overview">
        <button className="text-button deck-back" onClick={() => setSelectedDeckId(null)}><ChevronLeft size={17} /> Todos os baralhos</button>
        <div className="deck-overview-hero">
          <div><span className="eyebrow">BARALHO</span><h1 id="flashcards-title">{deckPath(selectedDeck, collection.decks)}</h1><p>{selectedDeck.description || "Revise no ritmo certo com agendamento FSRS."}</p></div>
          <div className="deck-overview-actions"><button onClick={() => openNoteEditor(null, selectedDeck.id)}><Plus size={17} /> Adicionar</button><button onClick={() => openDeckEditor(selectedDeck)}><Settings2 size={17} /> Opções</button></div>
        </div>
        <div className="deck-count-grid">
          <div><span className="count-new">{selectedCounts.new}</span><small>Novos</small></div>
          <div><span className="count-learn">{selectedCounts.learning}</span><small>Aprendendo</small></div>
          <div><span className="count-review">{selectedCounts.review}</span><small>Para revisar</small></div>
        </div>
        <button className="study-now-button" disabled={!selectedCounts.total} onClick={() => startStudy(selectedDeck)}><Play size={20} /> {selectedCounts.total ? "Estudar agora" : "Parabéns! Você terminou por hoje"}</button>
        <div className="deck-secondary-actions"><button onClick={() => setCustomStudyOpen(true)}><Shuffle size={16} /> Estudo personalizado</button><button onClick={() => showPage("browse")}><Search size={16} /> Explorar cartões</button><button onClick={() => void updateCards(collection.cards.filter((card) => card.buriedUntil).map((card) => card.id), (card) => ({ ...card, buriedUntil: null, updatedAt: Date.now() }))}>Reexibir cartões ocultos</button></div>
        <RecentActivity reviews={collection.reviews.filter((review) => descendantDeckIds(selectedDeck.id, collection.decks).has(review.deckId))} />
      </section>}

      {page === "browse" && <FlashcardBrowser collection={collection} cards={browseCards} query={browseQuery} setQuery={setBrowseQuery} selected={selectedCardIds} setSelected={setSelectedCardIds} onEditNote={(note) => openNoteEditor(note)} onSuspend={() => void bulkSuspend()} onFlag={(flag) => void bulkFlag(flag)} onMove={(deckId) => void bulkMove(deckId)} onDelete={() => void bulkDelete()} onReset={() => void resetSelected()} onSetDue={() => void setSelectedDue()} onAddTags={() => void bulkAddTags()} onRemoveTags={() => void bulkRemoveTags()} onFindReplace={() => void bulkFindReplace()} onChangeNoteType={(id) => void bulkChangeNoteType(id)} onReposition={() => void bulkReposition()} onFindDuplicates={findDuplicates} />}

      {page === "stats" && <StatsView collection={collection} />}

      {page === "types" && <NoteTypesView collection={collection} onEdit={editNoteType} onCreate={createNoteType} onDelete={(type) => void removeNoteType(type)} />}

      {deckDialog && <DeckDialog deck={deckDialog} setDeck={setDeckDialog} decks={collection.decks} saving={saving} error={dialogError} onClose={() => setDeckDialog(null)} onSubmit={() => void submitDeck()} />}
      {noteDialog && noteEditorDialog()}
      {typeDraft && <NoteTypeDialog type={typeDraft} setType={setTypeDraft} decks={collection.decks} saving={saving} error={dialogError} onClose={() => setTypeDraft(null)} onSubmit={() => void submitNoteType()} />}
      {customStudyOpen && selectedDeck && <CustomStudyDialog onClose={() => setCustomStudyOpen(false)} onStart={(query) => startStudy(selectedDeck, query)} />}
    </section>
  );

  function noteEditorDialog() {
    if (!noteDialog) return null;
    const noteType = collection.noteTypes.find((type) => type.id === noteDialog.noteTypeId) || collection.noteTypes[0];
    const previewNote = { ...noteDialog, fields: noteDraftFields, tags: normalizeTags(noteTags) };
    const previewCard = noteType ? reconcileNoteCards(previewNote, noteType, collection.cards.filter((card) => card.noteId === noteDialog.id)).cards[0] : null;
    const referencedMedia = collection.media.filter((media) => Object.values(noteDraftFields).some((value) => value.includes(`media:${media.id}`)));
    return <div className="modal-backdrop flash-modal-backdrop" role="presentation">
      <section className="modal-card note-editor-modal" role="dialog" aria-modal="true" aria-labelledby="note-editor-title">
        <button className="modal-close" onClick={() => setNoteDialog(null)} aria-label="Fechar"><X size={20} /></button>
        <span className="eyebrow">{collection.notes.some((note) => note.id === noteDialog.id) ? "EDITAR NOTA" : "NOVA NOTA"}</span>
        <h2 id="note-editor-title">Criar cartões</h2>
        <div className="note-editor-selectors">
          <label><span>Tipo de nota</span><select value={noteDialog.noteTypeId} onChange={(event) => changeNoteType(event.target.value)}>{collection.noteTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}</select></label>
          <label><span>Baralho</span><select value={noteDialog.deckId} onChange={(event) => setNoteDialog({ ...noteDialog, deckId: event.target.value })}>{collection.decks.filter((deck) => deck.kind === "standard").map((deck) => <option key={deck.id} value={deck.id}>{deckPath(deck, collection.decks)}</option>)}</select></label>
        </div>
        <div className="note-fields">
          {noteType?.fields.map((field) => <label key={field.id}>
            <span>{field.name}{noteType.kind === "cloze" && field.ordinal === 0 && <button type="button" onClick={() => insertCloze(field.name)}>Cloze</button>}</span>
            <textarea
              dir={field.rtl ? "rtl" : "auto"}
              value={noteDraftFields[field.name] || ""}
              onFocus={() => setActiveNoteField(field.name)}
              onChange={(event) => setNoteDraftFields((fields) => ({ ...fields, [field.name]: event.target.value }))}
              rows={field.ordinal === 0 ? 5 : 3}
            />
          </label>)}
        </div>
        <div className="note-editor-tools">
          <button type="button" className="ghost-button" disabled={saving} onClick={() => mediaRef.current?.click()}><FileUp size={15} /> Anexar mídia</button>
          <span>Imagem, áudio ou vídeo no campo <strong>{activeNoteField || noteType?.fields[0]?.name}</strong></span>
          <input ref={mediaRef} type="file" accept="image/*,audio/*,video/*" multiple hidden onChange={(event) => void attachMedia(event)} />
        </div>
        {referencedMedia.length > 0 && <div className="note-media-list" aria-label="Mídias da nota">
          {referencedMedia.map((media) => <div key={media.id}><Volume2 size={14} /><span>{media.name}</span><small>{formatBytes(media.size)}</small><button type="button" onClick={() => void removeMedia(media)} aria-label={`Excluir ${media.name}`}><Trash2 size={14} /></button></div>)}
        </div>}
        <label className="tags-field"><span><Tag size={14} /> Tags</span><input value={noteTags} onChange={(event) => setNoteTags(event.target.value)} placeholder="japonês verbo capítulo-1" /></label>
        {previewCard && noteType && <details className="note-preview"><summary>Pré-visualizar cartão</summary><div className="preview-sides"><div><small>FRENTE</small><div dangerouslySetInnerHTML={{ __html: renderFlashcardSide({ card: previewCard, note: previewNote, noteType, media: collection.media, side: "front" }).html }} /></div><div><small>VERSO</small><div dangerouslySetInnerHTML={{ __html: renderFlashcardSide({ card: previewCard, note: previewNote, noteType, media: collection.media, side: "back" }).html }} /></div></div></details>}
        {dialogError && <div className="form-error" role="alert">{dialogError}</div>}
        <div className="modal-actions"><button className="ghost-button" onClick={() => setNoteDialog(null)}>Cancelar</button><button className="align-button" disabled={saving} onClick={() => void submitNote()}>{saving ? <LoaderCircle className="spin" size={17} /> : <Check size={17} />} Salvar nota</button></div>
      </section>
    </div>;
  }
}

function TypedAnswerResult({ typed, expected }: { typed: string; expected: string }) {
  const comparison = compareTypedAnswer(typed, expected);
  const render = (segments: Array<{ text: string; match: boolean }>) => segments.map((segment, index) => <span className={segment.match ? "is-match" : "is-different"} key={`${index}-${segment.text}`}>{segment.text}</span>);
  return <div className={`typed-answer-result ${comparison.correct ? "is-correct" : "is-incorrect"}`}>
    <span>{comparison.correct ? "Resposta correta" : "Sua resposta"}</span>
    <strong>{render(comparison.typed)}</strong>
    {!comparison.correct && <><span>Resposta esperada</span><strong>{render(comparison.expected)}</strong></>}
  </div>;
}

let activeFlashcardMedia: HTMLAudioElement[] = [];

async function playFlashcardAudio(cues: FlashcardAudioCue[]) {
  stopFlashcardAudio();
  for (const cue of cues) {
    if (cue.kind === "tts") await playSpeechCue(cue);
    else {
      const audio = new Audio(cue.source);
      activeFlashcardMedia.push(audio);
      await audio.play().catch(() => undefined);
      await new Promise<void>((resolve) => {
        audio.addEventListener("ended", () => resolve(), { once: true });
        audio.addEventListener("error", () => resolve(), { once: true });
      });
    }
  }
}

function playSpeechCue(cue: Extract<FlashcardAudioCue, { kind: "tts" }>) {
  return new Promise<void>((resolve) => {
    if (!("speechSynthesis" in window)) return resolve();
    const utterance = new SpeechSynthesisUtterance(cue.text);
    utterance.lang = cue.language;
    utterance.rate = cue.speed;
    const voices = window.speechSynthesis.getVoices();
    utterance.voice = voices.find((voice) => cue.voices.some((name) => voice.name.toLocaleLowerCase().includes(name.toLocaleLowerCase())))
      || voices.find((voice) => voice.lang.toLocaleLowerCase().startsWith(cue.language.toLocaleLowerCase().split("-")[0]))
      || null;
    utterance.addEventListener("end", () => resolve(), { once: true });
    utterance.addEventListener("error", () => resolve(), { once: true });
    window.speechSynthesis.speak(utterance);
  });
}

function stopFlashcardAudio() {
  if (typeof window !== "undefined" && "speechSynthesis" in window) window.speechSynthesis.cancel();
  for (const audio of activeFlashcardMedia) {
    audio.pause();
    audio.currentTime = 0;
  }
  activeFlashcardMedia = [];
}

function DeckDashboard({ collection, onOpen, onCreate, onEdit, onDelete, onAddNote }: {
  collection: FlashcardCollection;
  onOpen: (id: string) => void;
  onCreate: (parentId?: string | null) => void;
  onEdit: (deck: FlashcardDeck) => void;
  onDelete: (deck: FlashcardDeck) => void;
  onAddNote: (deckId: string) => void;
}) {
  const flattened = flattenDecks(collection.decks);
  const today = collection.reviews.filter((review) => review.reviewedAt >= startOfToday());
  return <>
    <div className="flashcards-hero"><div><span className="eyebrow">FLASHCARDS</span><h1 id="flashcards-title">Aprenda o que você lê.</h1><p>Baralhos sincronizados, revisão FSRS e ferramentas completas para transformar leitura em memória.</p></div><div className="flashcards-summary"><strong>{collection.cards.length}</strong><span>cartões</span><i /><strong>{today.length}</strong><span>revisões hoje</span></div></div>
    <div className="deck-list-heading"><div><h2>Seus baralhos</h2><small>Novo <b>·</b> Aprendendo <b>·</b> Revisar</small></div><button onClick={() => onCreate(null)}><FolderPlus size={17} /> Criar baralho</button></div>
    <div className="deck-table">
      <div className="deck-table-header"><span>Baralho</span><span>Novo</span><span>Aprendendo</span><span>Revisar</span><span /></div>
      {flattened.map(({ deck, depth }) => {
        const counts = deckCounts(collection, deck.id);
        return <div className="deck-row" key={deck.id}>
          <button className="deck-name" style={{ paddingLeft: `${18 + depth * 24}px` }} onClick={() => onOpen(deck.id)}><Layers3 size={17} /><span><strong>{deck.name}</strong>{deck.kind === "filtered" && <small>Filtrado</small>}</span></button>
          <span className="count-new">{counts.new}</span><span className="count-learn">{counts.learning}</span><span className="count-review">{counts.review}</span>
          <div className="deck-row-actions"><button onClick={() => onAddNote(deck.id)} aria-label={`Adicionar cartão a ${deck.name}`}><CirclePlus size={16} /></button><button onClick={() => onCreate(deck.id)} aria-label={`Criar subbaralho em ${deck.name}`}><FolderPlus size={16} /></button><button onClick={() => onEdit(deck)} aria-label={`Editar ${deck.name}`}><Settings2 size={16} /></button>{deck.id !== "deck-default" && <button onClick={() => onDelete(deck)} aria-label={`Excluir ${deck.name}`}><Trash2 size={16} /></button>}</div>
        </div>;
      })}
    </div>
  </>;
}

function StatsView({ collection }: { collection: FlashcardCollection }) {
  const [deckId, setDeckId] = useState("");
  const [periodDays, setPeriodDays] = useState(0);
  const deckIds = deckId ? descendantDeckIds(deckId, collection.decks) : new Set(collection.decks.map((deck) => deck.id));
  const since = periodDays ? startOfToday() - (periodDays - 1) * 86_400_000 : 0;
  const cards = collection.cards.filter((card) => deckIds.has(card.deckId));
  const reviews = collection.reviews.filter((review) => deckIds.has(review.deckId) && review.reviewedAt >= since);
  const reviewsToday = reviews.filter((review) => review.reviewedAt >= startOfToday());
  const todayMinutes = reviewsToday.reduce((sum, review) => sum + review.durationMs, 0) / 60_000;
  const retention = reviews.length ? Math.round(reviews.filter((review) => review.rating > 1).length / reviews.length * 100) : 0;
  const averageSeconds = reviews.length ? reviews.reduce((sum, review) => sum + review.durationMs, 0) / reviews.length / 1_000 : 0;
  const counts = {
    new: cards.filter((card) => card.state === "new" && !card.suspended).length,
    learning: cards.filter((card) => ["learning", "relearning"].includes(card.state) && !card.suspended).length,
    young: cards.filter((card) => card.state === "review" && card.scheduledDays < 21 && !card.suspended).length,
    mature: cards.filter((card) => card.state === "review" && card.scheduledDays >= 21 && !card.suspended).length,
    suspended: cards.filter((card) => card.suspended).length,
  };
  const future = Array.from({ length: 14 }, (_, day) => cards.filter((card) => !card.suspended && card.due >= startOfToday() + day * 86_400_000 && card.due < startOfToday() + (day + 1) * 86_400_000).length);
  const maxFuture = Math.max(1, ...future);
  const activity = Array.from({ length: 28 }, (_, offset) => {
    const day = startOfToday() - (27 - offset) * 86_400_000;
    return reviews.filter((review) => review.reviewedAt >= day && review.reviewedAt < day + 86_400_000).length;
  });
  const maxActivity = Math.max(1, ...activity);
  return <section className="stats-view">
    <div className="stats-heading"><div><span className="eyebrow">ESTATÍSTICAS</span><h1>Seu aprendizado em números</h1><p>Histórico de revisões, retenção e previsão da carga futura.</p></div><div className="stats-filters"><select aria-label="Baralho das estatísticas" value={deckId} onChange={(event) => setDeckId(event.target.value)}><option value="">Toda a coleção</option>{collection.decks.filter((deck) => deck.kind === "standard").map((deck) => <option key={deck.id} value={deck.id}>{deckPath(deck, collection.decks)}</option>)}</select><select aria-label="Período das estatísticas" value={periodDays} onChange={(event) => setPeriodDays(Number(event.target.value))}><option value="0">Todo o período</option><option value="30">Últimos 30 dias</option><option value="90">Últimos 3 meses</option><option value="365">Último ano</option></select></div></div>
    <div className="stats-kpis"><div><strong>{reviewsToday.length}</strong><span>respostas hoje</span></div><div><strong>{retention}%</strong><span>retenção no período</span></div><div><strong>{todayMinutes.toFixed(1)} min</strong><span>tempo hoje</span></div><div><strong>{averageSeconds.toFixed(1)} s</strong><span>média por resposta</span></div></div>
    <div className="stats-grid"><article><h2>Cartões</h2><div className="card-count-bars">{Object.entries(counts).map(([label, count]) => <div key={label}><span>{statsStateLabel(label)}</span><i><b className={`bar-${label}`} style={{ width: `${cards.length ? count / cards.length * 100 : 0}%` }} /></i><strong>{count}</strong></div>)}</div></article><article><h2>Próximas revisões</h2><div className="future-chart">{future.map((count, day) => <div key={day} title={`${count} cartão(s)`}><i style={{ height: `${Math.max(3, count / maxFuture * 100)}%` }} /><small>{day === 0 ? "Hoje" : day}</small></div>)}</div></article><article className="activity-card"><h2>Atividade — 28 dias</h2><div className="activity-grid">{activity.map((count, index) => <span key={index} style={{ opacity: count ? 0.25 + count / maxActivity * 0.75 : 0.08 }} title={`${count} revisões`} />)}</div></article><article><h2>Botões de resposta</h2><div className="answer-stats">{[1, 2, 3, 4].map((rating) => { const count = reviews.filter((review) => review.rating === rating).length; return <div key={rating}><span>{["Novamente", "Difícil", "Bom", "Fácil"][rating - 1]}</span><strong>{count}</strong><small>{reviews.length ? Math.round(count / reviews.length * 100) : 0}%</small></div>; })}</div></article></div>
  </section>;
}

function NoteTypesView({ collection, onEdit, onCreate, onDelete }: { collection: FlashcardCollection; onEdit: (type: FlashcardNoteType) => void; onCreate: (kind: FlashcardNoteType["kind"]) => void; onDelete: (type: FlashcardNoteType) => void }) {
  return <section className="note-types-view"><div className="note-types-heading"><div><span className="eyebrow">MODELOS</span><h1>Tipos de nota</h1><p>Campos e modelos determinam quantos cartões cada nota cria.</p></div><div><button onClick={() => onCreate("standard")}><Plus size={16} /> Tipo padrão</button><button onClick={() => onCreate("cloze")}><Plus size={16} /> Tipo cloze</button></div></div><div className="note-type-grid">{collection.noteTypes.map((type) => <article key={type.id}><span className="note-type-icon"><BookCopy size={20} /></span><div><strong>{type.name}</strong><small>{type.kind === "cloze" ? "Cloze" : `${type.templates.length} modelo(s)`} · {type.fields.length} campos</small><span>{collection.notes.filter((note) => note.noteTypeId === type.id).length} notas</span></div><button onClick={() => onEdit(type)}><Edit3 size={16} /> Editar</button>{![BASIC_NOTE_TYPE_ID, CLOZE_NOTE_TYPE_ID].includes(type.id) && <button className="danger-action" onClick={() => onDelete(type)}><Trash2 size={15} /></button>}</article>)}</div></section>;
}

function DeckDialog({ deck, setDeck, decks, saving, error, onClose, onSubmit }: { deck: FlashcardDeck; setDeck: (deck: FlashcardDeck | null) => void; decks: FlashcardDeck[]; saving: boolean; error: string; onClose: () => void; onSubmit: () => void }) {
  const excluded = descendantDeckIds(deck.id, decks);
  const updateOptions = (changes: Partial<FlashcardDeck["options"]>) => setDeck({ ...deck, options: { ...deck.options, ...changes } });
  return <div className="modal-backdrop flash-modal-backdrop" role="presentation">
    <section className="modal-card deck-dialog" role="dialog" aria-modal="true" aria-labelledby="deck-dialog-title">
      <button className="modal-close" onClick={onClose} aria-label="Fechar"><X size={20} /></button>
      <span className="eyebrow">BARALHO E AGENDAMENTO</span>
      <h2 id="deck-dialog-title">{decks.some((candidate) => candidate.id === deck.id) ? "Editar baralho" : "Criar baralho"}</h2>
      <div className="deck-dialog-grid">
        <label><span>Nome</span><input autoFocus value={deck.name} onChange={(event) => setDeck({ ...deck, name: event.target.value })} placeholder="Ex.: Japonês::Vocabulário" /></label>
        <label><span>Tipo</span><select value={deck.kind} onChange={(event) => setDeck({ ...deck, kind: event.target.value as FlashcardDeck["kind"] })}><option value="standard">Padrão</option><option value="filtered">Filtrado / estudo personalizado</option></select></label>
        <label><span>Baralho pai</span><select value={deck.parentId || ""} onChange={(event) => setDeck({ ...deck, parentId: event.target.value || null })}><option value="">Nenhum</option>{decks.filter((candidate) => !excluded.has(candidate.id)).map((candidate) => <option key={candidate.id} value={candidate.id}>{deckPath(candidate, decks)}</option>)}</select></label>
        <label><span>Predefinição de opções</span><input value={deck.optionsPresetName} onChange={(event) => setDeck({ ...deck, optionsPresetName: event.target.value })} placeholder="Padrão" /></label>
        <label className="full"><span>Descrição</span><textarea value={deck.description} onChange={(event) => setDeck({ ...deck, description: event.target.value })} rows={2} /></label>
        {deck.kind === "filtered" && <>
          <label className="full"><span>Filtro de busca</span><input value={deck.filter} onChange={(event) => setDeck({ ...deck, filter: event.target.value })} placeholder="tag:prova is:due OR rated:1:1" /></label>
          <label><span>Limite de cartões</span><input type="number" min="1" value={deck.filterLimit} onChange={(event) => setDeck({ ...deck, filterLimit: Number(event.target.value) })} /></label>
          <label className="checkbox-field"><input type="checkbox" checked={deck.rescheduleFilteredCards} onChange={(event) => setDeck({ ...deck, rescheduleFilteredCards: event.target.checked })} /><span>Atualizar agendamento ao responder</span></label>
        </>}
        <div className="deck-options-divider full"><span>LIMITES E ETAPAS</span></div>
        <label><span>Novos por dia</span><input type="number" min="0" value={deck.options.newCardsPerDay} onChange={(event) => updateOptions({ newCardsPerDay: Number(event.target.value) })} /></label>
        <label><span>Revisões máximas/dia</span><input type="number" min="0" value={deck.options.maximumReviewsPerDay} onChange={(event) => updateOptions({ maximumReviewsPerDay: Number(event.target.value) })} /></label>
        <label><span>Etapas de aprendizado (min)</span><input value={deck.options.learningStepsMinutes.join(" ")} onChange={(event) => updateOptions({ learningStepsMinutes: numberList(event.target.value) })} /></label>
        <label><span>Etapas de reaprendizado (min)</span><input value={deck.options.relearningStepsMinutes.join(" ")} onChange={(event) => updateOptions({ relearningStepsMinutes: numberList(event.target.value) })} /></label>
        <label><span>Intervalo de graduação (dias)</span><input type="number" min="1" value={deck.options.graduatingIntervalDays} onChange={(event) => updateOptions({ graduatingIntervalDays: Number(event.target.value) })} /></label>
        <label><span>Intervalo Fácil (dias)</span><input type="number" min="1" value={deck.options.easyIntervalDays} onChange={(event) => updateOptions({ easyIntervalDays: Number(event.target.value) })} /></label>
        <label><span>Ação para sanguessugas</span><select value={deck.options.leechAction} onChange={(event) => updateOptions({ leechAction: event.target.value as FlashcardDeck["options"]["leechAction"] })}><option value="suspend">Marcar e suspender</option><option value="tag">Somente marcar</option></select></label>
        <label><span>Lapsos até sanguessuga</span><input type="number" min="1" value={deck.options.leechThreshold} onChange={(event) => updateOptions({ leechThreshold: Number(event.target.value) })} /></label>
        <div className="deck-options-divider full"><span>AGENDADOR</span></div>
        <label><span>Algoritmo</span><select value={deck.options.scheduler} onChange={(event) => updateOptions({ scheduler: event.target.value as FlashcardDeck["options"]["scheduler"] })}><option value="fsrs">FSRS</option><option value="sm2">Legado SM-2</option></select></label>
        {deck.options.scheduler === "fsrs" ? <><label><span>Retenção desejada FSRS</span><input type="number" min="0.7" max="0.99" step="0.01" value={deck.options.desiredRetention} onChange={(event) => updateOptions({ desiredRetention: Number(event.target.value) })} /></label><label className="full"><span>Parâmetros FSRS personalizados</span><input value={deck.options.fsrsParameters.join(" ")} onChange={(event) => updateOptions({ fsrsParameters: signedNumberList(event.target.value) })} placeholder="Vazio = parâmetros recomendados" /></label></> : <><label><span>Facilidade inicial</span><input type="number" min="1.3" max="5" step="0.05" value={deck.options.startingEase} onChange={(event) => updateOptions({ startingEase: Number(event.target.value) })} /></label><label><span>Bônus Fácil</span><input type="number" min="1" step="0.05" value={deck.options.easyBonus} onChange={(event) => updateOptions({ easyBonus: Number(event.target.value) })} /></label><label><span>Multiplicador Difícil</span><input type="number" min="0" step="0.05" value={deck.options.hardInterval} onChange={(event) => updateOptions({ hardInterval: Number(event.target.value) })} /></label><label><span>Novo intervalo após lapso</span><input type="number" min="0" max="1" step="0.05" value={deck.options.newInterval} onChange={(event) => updateOptions({ newInterval: Number(event.target.value) })} /></label><label><span>Modificador de intervalo</span><input type="number" min="0.1" step="0.05" value={deck.options.intervalModifier} onChange={(event) => updateOptions({ intervalModifier: Number(event.target.value) })} /></label></>}
        <label><span>Intervalo máximo (dias)</span><input type="number" min="1" value={deck.options.maximumIntervalDays} onChange={(event) => updateOptions({ maximumIntervalDays: Number(event.target.value) })} /></label>
        <label><span>Ordem dos novos</span><select value={deck.options.newOrder} onChange={(event) => updateOptions({ newOrder: event.target.value as FlashcardDeck["options"]["newOrder"] })}><option value="added">Adicionados primeiro</option><option value="reverse-added">Adicionados por último</option><option value="random">Aleatória</option></select></label>
        <label><span>Ordem das revisões</span><select value={deck.options.reviewOrder} onChange={(event) => updateOptions({ reviewOrder: event.target.value as FlashcardDeck["options"]["reviewOrder"] })}><option value="due">Mais atrasados primeiro</option><option value="random">Aleatória</option><option value="interval-ascending">Intervalo crescente</option><option value="interval-descending">Intervalo decrescente</option></select></label>
        <label><span>Novos e revisões</span><select value={deck.options.newReviewMix} onChange={(event) => updateOptions({ newReviewMix: event.target.value as FlashcardDeck["options"]["newReviewMix"] })}><option value="mix">Misturar</option><option value="new-first">Novos primeiro</option><option value="review-first">Revisões primeiro</option></select></label>
        <label className="checkbox-field"><input type="checkbox" checked={deck.options.buryNewSiblings} onChange={(event) => updateOptions({ buryNewSiblings: event.target.checked })} /><span>Ocultar novos irmãos</span></label>
        <label className="checkbox-field"><input type="checkbox" checked={deck.options.buryReviewSiblings} onChange={(event) => updateOptions({ buryReviewSiblings: event.target.checked })} /><span>Ocultar irmãos em revisão</span></label>
        <label className="checkbox-field"><input type="checkbox" checked={deck.options.showAnswerTimer} onChange={(event) => updateOptions({ showAnswerTimer: event.target.checked })} /><span>Mostrar cronômetro</span></label>
        <label className="checkbox-field"><input type="checkbox" checked={deck.options.autoPlayAudio} onChange={(event) => updateOptions({ autoPlayAudio: event.target.checked })} /><span>Reproduzir áudio automaticamente</span></label>
        <label><span>Limite de tempo da resposta (s)</span><input type="number" min="0" value={deck.options.maximumAnswerSeconds} onChange={(event) => updateOptions({ maximumAnswerSeconds: Number(event.target.value) })} /></label>
        <label><span>Mostrar resposta automaticamente após (s)</span><input type="number" min="0" value={deck.options.autoAdvanceSeconds} onChange={(event) => updateOptions({ autoAdvanceSeconds: Number(event.target.value) })} /></label>
      </div>
      <p className="preset-note">Alterações são aplicadas a todos os baralhos que usam a predefinição “{deck.optionsPresetName || "Padrão"}”.</p>
      {error && <div className="form-error">{error}</div>}
      <div className="modal-actions"><button className="ghost-button" onClick={onClose}>Cancelar</button><button className="align-button" disabled={saving} onClick={onSubmit}>{saving ? <LoaderCircle className="spin" size={17} /> : <Check size={17} />} Salvar baralho</button></div>
    </section>
  </div>;
}

function CustomStudyDialog({ onClose, onStart }: { onClose: () => void; onStart: (query: string) => void }) {
  const [mode, setMode] = useState("is:due");
  const [days, setDays] = useState(7);
  const [query, setQuery] = useState("");
  const resolved = mode === "ahead" ? `prop:due<=${days}` : mode === "forgotten" ? "rated:1:1" : mode === "search" ? query : mode;
  return <div className="modal-backdrop flash-modal-backdrop"><section className="modal-card custom-study-dialog" role="dialog" aria-modal="true"><button className="modal-close" onClick={onClose} aria-label="Fechar"><X size={20} /></button><span className="eyebrow">ESTUDO PERSONALIZADO</span><h2>Escolha o que revisar</h2><label><input type="radio" checked={mode === "is:due"} onChange={() => setMode("is:due")} /> Aumentar o limite de revisões de hoje</label><label><input type="radio" checked={mode === "is:new"} onChange={() => setMode("is:new")} /> Estudar mais cartões novos</label><label><input type="radio" checked={mode === "forgotten"} onChange={() => setMode("forgotten")} /> Rever cartões esquecidos hoje</label><label><input type="radio" checked={mode === "ahead"} onChange={() => setMode("ahead")} /> Revisar antecipadamente <input type="number" min="1" value={days} onChange={(event) => setDays(Number(event.target.value))} /> dias</label><label><input type="radio" checked={mode === "search"} onChange={() => setMode("search")} /> Buscar por tags ou propriedades</label>{mode === "search" && <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="tag:prova OR is:learning" />}<div className="modal-actions"><button className="ghost-button" onClick={onClose}>Cancelar</button><button className="align-button" onClick={() => onStart(resolved)}><Shuffle size={17} /> Criar sessão</button></div></section></div>;
}

function CardInfoDialog({ card, reviews, onClose }: { card: Flashcard; reviews: FlashcardReview[]; onClose: () => void }) {
  return <div className="modal-backdrop flash-modal-backdrop"><section className="modal-card card-info-dialog" role="dialog" aria-modal="true"><button className="modal-close" onClick={onClose} aria-label="Fechar"><X size={20} /></button><span className="eyebrow">INFORMAÇÕES</span><h2>Histórico do cartão</h2><dl><div><dt>Estado</dt><dd>{stateLabel(card)}</dd></div><div><dt>Intervalo</dt><dd>{card.scheduledDays} dias</dd></div><div><dt>Estabilidade</dt><dd>{card.stability.toFixed(2)}</dd></div><div><dt>Dificuldade</dt><dd>{card.difficulty.toFixed(2)}</dd></div><div><dt>Repetições</dt><dd>{card.reps}</dd></div><div><dt>Lapsos</dt><dd>{card.lapses}</dd></div><div><dt>Próxima revisão</dt><dd>{new Date(card.due).toLocaleString("pt-BR")}</dd></div></dl><div className="review-history"><div><strong>Data</strong><strong>Resposta</strong><strong>Intervalo</strong><strong>Tempo</strong></div>{[...reviews].reverse().slice(0, 100).map((review) => <div key={review.id}><span>{new Date(review.reviewedAt).toLocaleString("pt-BR")}</span><span>{["", "Novamente", "Difícil", "Bom", "Fácil"][review.rating]}</span><span>{review.scheduledDaysAfter}d</span><span>{(review.durationMs / 1000).toFixed(1)}s</span></div>)}</div></section></div>;
}

function RecentActivity({ reviews }: { reviews: FlashcardReview[] }) {
  const recent = reviews.filter((review) => review.reviewedAt >= Date.now() - 7 * 86_400_000);
  return <div className="recent-activity"><div><span className="eyebrow">ÚLTIMOS 7 DIAS</span><h2>{recent.length} revisões</h2></div><div className="mini-activity">{Array.from({ length: 7 }, (_, offset) => { const day = startOfToday() - (6 - offset) * 86_400_000; const count = recent.filter((review) => review.reviewedAt >= day && review.reviewedAt < day + 86_400_000).length; return <span key={offset}><i style={{ height: `${Math.max(4, Math.min(100, count * 8))}%` }} /><small>{new Date(day).toLocaleDateString("pt-BR", { weekday: "short" }).slice(0, 3)}</small></span>; })}</div></div>;
}

function flattenDecks(decks: FlashcardDeck[]) {
  const result: Array<{ deck: FlashcardDeck; depth: number }> = [];
  const visit = (parentId: string | null, depth: number) => decks.filter((deck) => deck.parentId === parentId).sort((a, b) => a.name.localeCompare(b.name)).forEach((deck) => { result.push({ deck, depth }); visit(deck.id, depth + 1); });
  visit(null, 0);
  for (const deck of decks) if (!result.some((item) => item.deck.id === deck.id)) result.push({ deck, depth: 0 });
  return result;
}

function upsert<T extends { id: string }>(items: T[], item: T) {
  return items.some((candidate) => candidate.id === item.id) ? items.map((candidate) => candidate.id === item.id ? item : candidate) : [...items, item];
}

function normalizeTags(value: string) {
  return [...new Set(value.split(/[\s,]+/).map((tag) => tag.trim()).filter(Boolean))];
}

function parseReplacementExpression(value: string) {
  const expression = value.match(/^\/(.+)\/([gimsuy]*)$/);
  if (expression) {
    try {
      return new RegExp(expression[1], expression[2].includes("g") ? expression[2] : `${expression[2]}g`);
    } catch {
      // Fall back to a literal replacement when the regular expression is invalid.
    }
  }
  return new RegExp(value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g");
}

function numberList(value: string) {
  return value.split(/[\s,]+/).map(Number).filter((number) => Number.isFinite(number) && number >= 0);
}
function signedNumberList(value: string) {
  return value.split(/[\s,]+/).map(Number).filter((number) => Number.isFinite(number));
}

function startOfToday() { const date = new Date(); date.setHours(0, 0, 0, 0); return date.getTime(); }
function stateLabel(card: Flashcard) { if (card.suspended) return "Suspenso"; if (card.buriedUntil && card.buriedUntil > Date.now()) return "Oculto"; return ({ new: "Novo", learning: "Aprendendo", review: "Revisão", relearning: "Reaprendendo" })[card.state]; }
function statsStateLabel(value: string) { return ({ new: "Novos", learning: "Aprendendo", young: "Jovens", mature: "Maduros", suspended: "Suspensos" } as Record<string, string>)[value] || value; }
function stripHtml(value: string) { return typeof DOMParser === "undefined" ? value.replace(/<[^>]+>/g, " ") : new DOMParser().parseFromString(value, "text/html").body.textContent || ""; }
function errorMessage(reason: unknown) { return reason instanceof Error ? reason.message : "Não foi possível concluir esta ação."; }
function tsvCell(value: string) { return value.replace(/\t/g, " ").replace(/\r?\n/g, "<br>"); }
function downloadBlob(blob: Blob, name: string) { const url = URL.createObjectURL(blob); const anchor = document.createElement("a"); anchor.href = url; anchor.download = name; anchor.click(); setTimeout(() => URL.revokeObjectURL(url), 0); }
function mediaMarkup(media: FlashcardMedia) {
  const reference = `media:${media.id}`;
  const name = escapeAttribute(media.name);
  if (media.contentType.startsWith("image/")) return `<img src="${reference}" alt="${name}">`;
  if (media.contentType.startsWith("audio/")) return `[sound:${reference}]`;
  if (media.contentType.startsWith("video/")) return `<video controls src="${reference}"></video>`;
  return `<a href="${reference}">${name}</a>`;
}
function removeMediaMarkup(value: string, mediaId: string) {
  const id = mediaId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return value
    .replace(new RegExp(`<img\\b[^>]*\\bsrc=["']media:${id}["'][^>]*>`, "gi"), "")
    .replace(new RegExp(`<video\\b[^>]*\\bsrc=["']media:${id}["'][^>]*>(?:<\\/video>)?`, "gi"), "")
    .replace(new RegExp(`<a\\b[^>]*\\bhref=["']media:${id}["'][^>]*>[\\s\\S]*?<\\/a>`, "gi"), "")
    .replace(new RegExp(`\\[sound:media:${id}\\]`, "gi"), "")
    .trim();
}
function replaceMediaReferences(value: string, mediaIds: Map<string, string>) {
  let next = value;
  for (const [before, after] of mediaIds) next = next.replaceAll(`media:${before}`, `media:${after}`);
  return next;
}
function escapeAttribute(value: string) { return value.replace(/[&<>"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[character] || character); }
function formatBytes(value: number) { return value < 1024 * 1024 ? `${Math.max(1, Math.round(value / 1024))} KB` : `${(value / (1024 * 1024)).toFixed(1)} MB`; }
